import { useCallback, useEffect, useMemo, useState } from 'react';
import { MdAssessment, MdFileDownload, MdPrint } from 'react-icons/md';
import PageHeader from '../../components/owner/PageHeader';
import {
  fetchExpensesForDateRange,
  fetchCoaZReportExpensesForDateRange,
  fetchCoaBankLedgerPaymentsForDateRange,
} from '../../services/admin/financeLedgerApi';
import {
  fetchPaymentsForDateRange,
  fetchGuestPasses,
} from '../../services/receptionist/receptionistApi';
import { ensureGymId, getCachedGymId } from '../../services/receptionist/gymContext';
import { isReceptionistPanelScope } from '../../services/gym/branchContext';
import { pkTodayKey } from '../../utils/pkTime';
import { usePanelToast } from '../../context/PanelToastContext';
import { loadAdminSettings } from './settings/settingsData';
import {
  buildBankDetail,
  buildZReport,
  expenseCalendarDay,
  loadBankCoaExpenses,
  loadManualExpenses,
  localDateKey,
  mapApiExpenseToManual,
  mergeManualExpenses,
} from './finance/financeData';
import {
  exportBankDetailExcel,
  exportBankDetailPdf,
} from './exportCashBookDetail';
import '../owner/ownerPages.css';
import './finance/finance.css';
import '../receptionist/receptionistPanel.css';

function formatReportDate(isoDay) {
  const day = String(isoDay || '').slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) return day || '—';
  const d = new Date(`${day}T12:00:00`);
  if (Number.isNaN(d.getTime())) return day;
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' }).replace(/ /g, '-');
}

function formatDisplayDate(isoDay) {
  const day = String(isoDay || '').slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) return '';
  const d = new Date(`${day}T12:00:00`);
  if (Number.isNaN(d.getTime())) return day;
  return `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`;
}

function formatPettyAmount(amount, { allowZero = false } = {}) {
  const n = Number(amount) || 0;
  if (!allowZero && n <= 0) return '';
  return n.toLocaleString('en-US');
}

function eachDay(from, to) {
  const start = String(from || '').slice(0, 10);
  const end = String(to || from || '').slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(start) || !/^\d{4}-\d{2}-\d{2}$/.test(end)) return [];
  const lo = start <= end ? start : end;
  const hi = start <= end ? end : start;
  const days = [];
  const cursor = new Date(`${lo}T12:00:00`);
  const last = new Date(`${hi}T12:00:00`);
  while (cursor <= last && days.length < 93) {
    days.push(localDateKey(cursor));
    cursor.setDate(cursor.getDate() + 1);
  }
  return days;
}

function mapGuestIncome(rows = []) {
  return (rows || []).map((g) => ({
    ...g,
    member: g.name || g.member || '—',
    amount: Number(g.amount) || Number(g.fee) || Number(g.price) || 0,
    date: g.startDate || g.date || g.createdAt,
    createdAt: g.createdAt || g.date || '',
    startDate: g.startDate || '',
    method: g.method || g.paymentMethod || 'cash',
    package: 'Guest Pass',
    duration: g.duration || g.passType || '',
    type: 'Guest Pass',
    source: 'guest_pass',
    status: g.status || 'completed',
    invoiceId: g.invoiceId || g.id || '',
    phone: g.phone || '',
  }));
}

export default function BankRangePage() {
  const toast = usePanelToast();
  const receptionistScope = isReceptionistPanelScope();
  const todayPk = pkTodayKey();
  const defaultDay = receptionistScope ? todayPk : localDateKey();

  const [gymId, setGymId] = useState(() => getCachedGymId() || '');
  const [draft, setDraft] = useState({ startDate: defaultDay, endDate: defaultDay });
  const [applied, setApplied] = useState({ startDate: defaultDay, endDate: defaultDay });
  const [detail, setDetail] = useState(() => buildBankDetail({ date: defaultDay }));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const gymTitle = useMemo(() => {
    const settings = loadAdminSettings(gymId);
    return (
      settings?.gym?.name?.trim()
      || settings?.softwareName?.trim()
      || 'OXYGYM HEALTH & FITNESS CLUB'
    ).toUpperCase();
  }, [gymId]);

  const headerDate = useMemo(() => {
    const from = applied.startDate;
    const to = applied.endDate || applied.startDate;
    if (from && to && from !== to) return `${formatReportDate(from)} → ${formatReportDate(to)}`;
    return formatReportDate(from || to);
  }, [applied.startDate, applied.endDate]);

  const loadRange = useCallback(async (startDate, endDate) => {
    const from = receptionistScope
      ? pkTodayKey()
      : String(startDate || localDateKey()).slice(0, 10);
    const to = receptionistScope
      ? pkTodayKey()
      : String(endDate || startDate || localDateKey()).slice(0, 10);
    const start = from <= to ? from : to;
    const end = from <= to ? to : from;

    try {
      setError('');
      setLoading(true);
      const gid = getCachedGymId() || await ensureGymId().catch(() => '');
      if (gid) setGymId(gid);

      const [
        paymentRows,
        guestRows,
        expenseRows,
        coaExpenseRows,
        coaBankRows,
      ] = await Promise.all([
        fetchPaymentsForDateRange(start, end).catch(() => []),
        fetchGuestPasses().catch(() => []),
        fetchExpensesForDateRange(start, end, gid || undefined).catch(() => []),
        fetchCoaZReportExpensesForDateRange(start, end, gid || undefined).catch(() => []),
        fetchCoaBankLedgerPaymentsForDateRange(start, end, gid || undefined).catch(() => []),
      ]);

      const inRange = (row) => {
        const d = expenseCalendarDay(row);
        return d && d >= start && d <= end;
      };
      const apiExpenses = (expenseRows || []).map(mapApiExpenseToManual).filter(inRange);
      const coaMapped = (coaExpenseRows || []).map(mapApiExpenseToManual).filter(inRange);
      const coaBankMapped = (coaBankRows || []).map(mapApiExpenseToManual).filter(inRange);
      const localCoa = eachDay(start, end).flatMap((day) => (
        (loadBankCoaExpenses(gid, day) || []).filter((row) => expenseCalendarDay(row) === day)
      ));
      const dayManualExpenses = (loadManualExpenses(gid) || []).filter(inRange);
      const allExpenses = mergeManualExpenses(
        mergeManualExpenses(
          mergeManualExpenses([...apiExpenses, ...coaMapped], coaBankMapped),
          localCoa,
        ),
        dayManualExpenses,
      );

      const guestIncome = mapGuestIncome(guestRows).filter((g) => {
        const d = String(g.startDate || g.date || g.createdAt || '').slice(0, 10);
        return d && d >= start && d <= end;
      });

      const dayZ = buildZReport({
        date: end,
        dateFrom: start,
        dateTo: end,
        payments: paymentRows,
        guestPassIncome: guestIncome,
        expenses: allExpenses,
        gymId: gid,
        paymentsAlreadyScoped: true,
      });

      const next = buildBankDetail({
        date: end,
        dateFrom: start,
        dateTo: end,
        payments: paymentRows,
        guestPassIncome: guestIncome,
        expenses: allExpenses,
        salesRows: dayZ.salesRows || [],
        openingBalance: 0,
      });
      setDetail(next);
    } catch (err) {
      setError(err.message || 'Failed to load bank detail');
      setDetail(buildBankDetail({ date: start, dateFrom: start, dateTo: end }));
    } finally {
      setLoading(false);
    }
  }, [receptionistScope]);

  useEffect(() => {
    loadRange(applied.startDate, applied.endDate);
  }, [applied.startDate, applied.endDate, loadRange]);

  useEffect(() => {
    if (!receptionistScope) return;
    if (draft.startDate !== todayPk || draft.endDate !== todayPk) {
      setDraft({ startDate: todayPk, endDate: todayPk });
    }
    if (applied.startDate !== todayPk || applied.endDate !== todayPk) {
      setApplied({ startDate: todayPk, endDate: todayPk });
    }
  }, [receptionistScope, todayPk, draft.startDate, draft.endDate, applied.startDate, applied.endDate]);

  const handleSearch = (e) => {
    e?.preventDefault?.();
    if (receptionistScope) {
      setApplied({ startDate: todayPk, endDate: todayPk });
      return;
    }
    let startDate = draft.startDate || '';
    let endDate = draft.endDate || '';
    if (startDate && endDate && endDate < startDate) endDate = startDate;
    setApplied({
      startDate: startDate || localDateKey(),
      endDate: endDate || startDate || localDateKey(),
    });
  };

  const handleClear = () => {
    const day = receptionistScope ? todayPk : localDateKey();
    setDraft({ startDate: day, endDate: day });
    setApplied({ startDate: day, endDate: day });
  };

  const handlePrint = () => {
    window.print();
  };

  const exportPayload = useMemo(() => ({
    detail,
    rangeLabel: headerDate,
    gymTitle,
  }), [detail, headerDate, gymTitle]);

  const handleExportExcel = () => {
    exportBankDetailExcel(exportPayload);
    toast.showSuccess('Excel download started.', 'Exported');
  };

  const handleExportPdf = () => {
    exportBankDetailPdf(exportPayload);
    toast.showSuccess('PDF download started.', 'Exported');
  };

  const bodyRows = useMemo(
    () => (detail.rows || []).filter((row) => row.kind !== 'opening' && row.kind !== 'closing'),
    [detail.rows],
  );

  return (
    <div className="rec-z-report">
      <PageHeader
        title="Bank"
        subtitle="Bank, card, and QR receipts plus bank ledger payments for the selected dates"
        variant="banner"
      >
        <span className="owner-badge owner-badge-info" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <MdAssessment aria-hidden />
          Bank
        </span>
        <button type="button" className="owner-btn owner-btn-secondary no-print" onClick={handlePrint}>
          <MdPrint aria-hidden /> Print
        </button>
        <button type="button" className="owner-btn owner-btn-secondary no-print" onClick={handleExportExcel}>
          <MdFileDownload aria-hidden /> Export Excel
        </button>
        <button type="button" className="owner-btn owner-btn-secondary no-print" onClick={handleExportPdf}>
          <MdFileDownload aria-hidden /> Export PDF
        </button>
      </PageHeader>

      <form className="panel-dash-filters no-print" onSubmit={handleSearch}>
        <div className="owner-form-group">
          <label className="owner-form-label" htmlFor="bank-start">Start date</label>
          <input
            id="bank-start"
            type="date"
            className="owner-form-input"
            value={receptionistScope ? todayPk : draft.startDate}
            max={draft.endDate || undefined}
            readOnly={receptionistScope}
            disabled={receptionistScope}
            onChange={(e) => {
              if (receptionistScope) return;
              setDraft((d) => ({ ...d, startDate: e.target.value }));
            }}
          />
        </div>
        <div className="owner-form-group">
          <label className="owner-form-label" htmlFor="bank-end">End date</label>
          <input
            id="bank-end"
            type="date"
            className="owner-form-input"
            value={receptionistScope ? todayPk : draft.endDate}
            min={draft.startDate || undefined}
            readOnly={receptionistScope}
            disabled={receptionistScope}
            onChange={(e) => {
              if (receptionistScope) return;
              setDraft((d) => ({ ...d, endDate: e.target.value }));
            }}
          />
        </div>
        <div className="panel-dash-filter-actions">
          <button type="submit" className="owner-btn owner-btn-primary owner-btn-sm">Search</button>
          <button type="button" className="owner-btn owner-btn-secondary owner-btn-sm" onClick={handleClear}>
            Clear
          </button>
        </div>
      </form>

      <p className="owner-placeholder-text panel-dash-applied no-print">
        Showing: {applied.startDate || '…'} → {applied.endDate || applied.startDate || '…'}
        {receptionistScope ? ' · today only' : ''}
      </p>

      {error && (
        <p className="owner-placeholder-text owner-mb-16" style={{ color: 'var(--owner-danger, #dc2626)' }}>
          {error}
        </p>
      )}
      {loading && <p className="owner-placeholder-text owner-mb-16">Loading bank detail…</p>}

      <div id="z-detailed-report-print">
        <div
          className="owner-card rec-panel-card admin-finance-section admin-z-sale-report admin-z-petty-report"
          id="z-bank-print"
        >
          <div className="admin-z-sale-header">
            <div className="admin-z-sale-brand">{gymTitle}</div>
            <div className="admin-z-sale-subtitle-row">
              <span className="admin-z-sale-subtitle">BANK DETAIL</span>
              <span className="admin-z-sale-date">{headerDate}</span>
            </div>
          </div>

          <table className="admin-z-sale-table admin-z-petty-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Description</th>
                <th>Receipts</th>
                <th>Payments</th>
                <th>Balance</th>
              </tr>
            </thead>
            <tbody>
              {(detail.rows || []).filter((row) => row.kind === 'opening').map((row) => (
                <tr key={row.id} className="admin-z-sale-footer-total-row">
                  <td>{row.date ? formatDisplayDate(row.date) : ''}</td>
                  <td className="admin-z-sale-summary-label">
                    <strong>{row.description}</strong>
                  </td>
                  <td className="admin-z-sale-amount">{formatPettyAmount(row.receipts)}</td>
                  <td className="admin-z-sale-amount">{formatPettyAmount(row.payments)}</td>
                  <td className="admin-z-sale-amount">
                    <strong>{formatPettyAmount(row.balance, { allowZero: true })}</strong>
                  </td>
                </tr>
              ))}

              {bodyRows.map((row) => (
                <tr key={row.id} className="admin-z-sale-member-row">
                  <td>{row.date ? formatDisplayDate(row.date) : ''}</td>
                  <td className="admin-z-petty-desc">{row.description}</td>
                  <td className="admin-z-sale-amount">{formatPettyAmount(row.receipts)}</td>
                  <td className="admin-z-sale-amount">{formatPettyAmount(row.payments)}</td>
                  <td className="admin-z-sale-amount">
                    <strong>{formatPettyAmount(row.balance, { allowZero: true })}</strong>
                  </td>
                </tr>
              ))}

              {!bodyRows.length && (
                <tr className="no-print">
                  <td colSpan={5} className="admin-z-sale-empty">
                    {loading ? 'Loading bank detail…' : 'No bank sales or ledger expenses for this range.'}
                  </td>
                </tr>
              )}

              {(detail.rows || []).filter((row) => row.kind === 'closing').map((row) => (
                <tr key={row.id} className="admin-z-sale-footer-total-row">
                  <td>{row.date ? formatDisplayDate(row.date) : ''}</td>
                  <td className="admin-z-sale-summary-label"><strong>{row.description}</strong></td>
                  <td className="admin-z-sale-amount">
                    <strong>{formatPettyAmount(row.receipts, { allowZero: true })}</strong>
                  </td>
                  <td className="admin-z-sale-amount">
                    <strong>{formatPettyAmount(row.payments, { allowZero: true })}</strong>
                  </td>
                  <td className="admin-z-sale-amount">
                    <strong>{formatPettyAmount(row.closingBalance ?? row.balance, { allowZero: true })}</strong>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
